/**
 * Reservation Settings Service
 *
 * Service layer for managing reservation system settings.
 * Admin can control hours, capacity, features, etc.
 */

import { supabase } from "./supabase";
import { logger } from "../utils/logger";

/**
 * Default reservation settings
 * Used as fallback when no settings exist in database
 */
const DEFAULT_SETTINGS = {
  opening_time: "11:00:00",
  closing_time: "23:00:00",
  time_slot_interval: 30,
  max_capacity_per_slot: 50,
  max_party_size: 20,
  min_party_size: 1,
  operating_days: [0, 1, 2, 3, 4, 5, 6],
  allow_same_day_booking: true,
  advance_booking_days: 30,
  enabled_occasions: [
    "birthday",
    "anniversary",
    "business",
    "date",
    "celebration",
    "casual",
  ],
  enabled_preferences: ["window", "quiet", "bar", "outdoor", "any"],
  blocked_dates: [],
  special_notice: null,
};

/**
 * Ensure default settings exist in database
 * Auto-creates settings row if missing
 *
 * @returns {Promise<boolean>} True if settings exist or were created successfully
 */
async function ensureDefaultSettings() {
  try {
    // Check if settings already exist
    const { data: existing } = await supabase
      .from("reservation_settings")
      .select("id")
      .maybeSingle();

    if (existing) {
      return true; // Settings already exist
    }

    // Create default settings
    logger.info("No reservation settings found, creating defaults...");
    const { error: insertError } = await supabase
      .from("reservation_settings")
      .insert([DEFAULT_SETTINGS]);

    if (insertError) {
      logger.error(
        "Failed to create default reservation settings:",
        insertError,
      );
      return false;
    }

    logger.info("Default reservation settings created successfully");
    return true;
  } catch (err) {
    logger.error("Error in ensureDefaultSettings:", err);
    return false;
  }
}

/**
 * Get current reservation settings
 * Auto-creates defaults if missing, gracefully falls back to defaults on error
 *
 * @returns {Promise<{success: boolean, data: Object|null, error: string|null}>}
 */
export async function getReservationSettings() {
  try {
    // First attempt: Try to fetch existing settings
    const { data, error } = await supabase
      .from("reservation_settings")
      .select("*")
      .maybeSingle();

    // Handle case where no rows exist (PGRST116 or similar)
    const isNoRowsError =
      error &&
      (error.code === "PGRST116" ||
        (error.message && error.message.toLowerCase().includes("no rows")));

    if (isNoRowsError || (!data && !error)) {
      // No settings found - try to create defaults
      logger.info("No reservation settings found, attempting auto-creation...");
      const created = await ensureDefaultSettings();

      if (created) {
        // Retry fetch after creation
        const { data: retryData, error: retryError } = await supabase
          .from("reservation_settings")
          .select("*")
          .maybeSingle();

        if (retryData) {
          logger.info("Successfully fetched auto-created settings");
          return {
            success: true,
            data: retryData,
            error: null,
          };
        }

        if (retryError) {
          logger.warn(
            "Retry fetch failed, falling back to defaults:",
            retryError,
          );
        }
      }

      // Creation failed or retry failed - return defaults as graceful fallback
      logger.warn("Using default settings as fallback");
      return {
        success: true,
        data: DEFAULT_SETTINGS,
        error: null,
      };
    }

    // Handle other errors (RLS, connection, etc.)
    if (error) {
      logger.error("Error fetching reservation settings:", error);
      // Return defaults instead of failing
      logger.warn("Returning default settings due to error");
      return {
        success: true,
        data: DEFAULT_SETTINGS,
        error: null,
      };
    }

    // Success - return fetched data
    return {
      success: true,
      data: data,
      error: null,
    };
  } catch (err) {
    logger.error("Unexpected error in getReservationSettings:", err);
    // Graceful degradation - return defaults
    logger.warn("Returning default settings due to unexpected error");
    return {
      success: true,
      data: DEFAULT_SETTINGS,
      error: null,
    };
  }
}

/**
 * Update reservation settings (admin only)
 * Ensures settings exist before updating
 *
 * @param {Object} settings - Updated settings
 * @returns {Promise<{success: boolean, error: string|null}>}
 */
export async function updateReservationSettings(settings) {
  try {
    // Ensure default settings exist first
    await ensureDefaultSettings();

    // Get the current settings ID (should only be one row)
    const { data: current, error: fetchError } = await supabase
      .from("reservation_settings")
      .select("id")
      .maybeSingle();

    if (fetchError) {
      logger.error("Error fetching settings for update:", fetchError);
      return {
        success: false,
        error: fetchError.message || "Failed to fetch settings",
      };
    }

    if (!current) {
      // Try one more time to create defaults
      logger.warn("Settings not found during update, attempting to create...");
      const created = await ensureDefaultSettings();

      if (!created) {
        return {
          success: false,
          error: "No settings record found and could not create one",
        };
      }

      // Retry fetch
      const { data: retryData } = await supabase
        .from("reservation_settings")
        .select("id")
        .maybeSingle();

      if (!retryData) {
        return {
          success: false,
          error: "Failed to create settings record",
        };
      }

      // Update settings using the newly created record
      const { error: updateError } = await supabase
        .from("reservation_settings")
        .update({
          ...settings,
          updated_at: new Date().toISOString(),
        })
        .eq("id", retryData.id);

      if (updateError) {
        logger.error("Error updating newly created settings:", updateError);
        return {
          success: false,
          error: updateError.message || "Failed to update settings",
        };
      }

      return {
        success: true,
        error: null,
      };
    }

    // Update settings using existing record
    const { error } = await supabase
      .from("reservation_settings")
      .update({
        ...settings,
        updated_at: new Date().toISOString(),
      })
      .eq("id", current.id);

    if (error) {
      logger.error("Error updating reservation settings:", error);
      return {
        success: false,
        error: error.message || "Failed to update settings",
      };
    }

    return {
      success: true,
      error: null,
    };
  } catch (err) {
    logger.error("Unexpected error in updateReservationSettings:", err);
    return {
      success: false,
      error: "An unexpected error occurred",
    };
  }
}

/**
 * Generate time slots based on current settings
 *
 * @param {Object} settings - Reservation settings
 * @returns {Array<string>} Array of time strings (HH:MM)
 */
export function generateTimeSlotsFromSettings(settings) {
  if (!settings) return [];

  const slots = [];
  const interval = settings.time_slot_interval || 30;

  // Parse opening and closing times
  const [openHour, openMin] = settings.opening_time.split(":").map(Number);
  const [closeHour, closeMin] = settings.closing_time.split(":").map(Number);

  let currentHour = openHour;
  let currentMin = openMin;

  while (
    currentHour < closeHour ||
    (currentHour === closeHour && currentMin < closeMin)
  ) {
    const timeString = `${currentHour.toString().padStart(2, "0")}:${currentMin.toString().padStart(2, "0")}`;
    slots.push(timeString);

    // Add interval
    currentMin += interval;
    if (currentMin >= 60) {
      currentMin -= 60;
      currentHour += 1;
    }
  }

  return slots;
}

/**
 * Check if a date is blocked
 *
 * @param {string} date - Date in YYYY-MM-DD format
 * @param {Array<string>} blockedDates - Array of blocked dates
 * @returns {boolean}
 */
export function isDateBlocked(date, blockedDates = []) {
  return blockedDates.includes(date);
}

/**
 * Check if a day of week is operating
 *
 * @param {Date} date - JavaScript Date object
 * @param {Array<number>} operatingDays - Array of operating day numbers
 * @returns {boolean}
 */
export function isDayOperating(date, operatingDays = [0, 1, 2, 3, 4, 5, 6]) {
  const dayOfWeek = date.getDay(); // 0 = Sunday, 6 = Saturday
  return operatingDays.includes(dayOfWeek);
}

/**
 * Get minimum allowed booking date based on settings
 *
 * @param {boolean} allowSameDayBooking - Whether same-day booking is allowed
 * @returns {Date}
 */
export function getMinBookingDate(allowSameDayBooking = true) {
  const today = new Date();

  if (allowSameDayBooking) {
    return today;
  }

  // Next day
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  return tomorrow;
}

/**
 * Get maximum allowed booking date based on settings
 *
 * @param {number} advanceBookingDays - Number of days in advance allowed
 * @returns {Date}
 */
export function getMaxBookingDate(advanceBookingDays = 30) {
  const today = new Date();
  const maxDate = new Date(today);
  maxDate.setDate(maxDate.getDate() + advanceBookingDays);
  return maxDate;
}

export default {
  getReservationSettings,
  updateReservationSettings,
  generateTimeSlotsFromSettings,
  isDateBlocked,
  isDayOperating,
  getMinBookingDate,
  getMaxBookingDate,
};
